import { newId } from './ids';
import type { Point } from './math';

export const ELEMENT_TYPES = [
  'stroke',
  'sticky',
  'text',
  'shape',
  'connector',
  'image',
  'stamp',
] as const;
export type ElementType = (typeof ELEMENT_TYPES)[number];

export const PEN_TOOLS = ['pen', 'marker', 'highlighter', 'brush'] as const;
export type PenTool = (typeof PEN_TOOLS)[number];

export const FONT_FAMILIES = ['sans', 'hand', 'serif', 'mono'] as const;
export type FontFamily = (typeof FONT_FAMILIES)[number];

export type TextAlign = 'left' | 'center' | 'right';

export const SHAPE_KINDS = [
  'rect',
  'roundRect',
  'ellipse',
  'triangle',
  'diamond',
  'hexagon',
  'star',
  'arrow',
  'speech',
] as const;
export type ShapeKind = (typeof SHAPE_KINDS)[number];

export type DashStyle = 'solid' | 'dashed' | 'dotted';
export type ConnectorRoute = 'straight' | 'curved' | 'elbow';
export type ConnectorCap = 'none' | 'arrow' | 'triangle' | 'circle';

/** Properties that only take one of a few values, by property name. */
export const ELEMENT_ENUMS: Readonly<Record<string, readonly string[]>> = {
  tool: PEN_TOOLS,
  font: FONT_FAMILIES,
  align: ['left', 'center', 'right'],
  kind: SHAPE_KINDS,
  dash: ['solid', 'dashed', 'dotted'],
  route: ['straight', 'curved', 'elbow'],
  startCap: ['none', 'arrow', 'triangle', 'circle'],
  endCap: ['none', 'arrow', 'triangle', 'circle'],
};

/** Properties that are sizes: a size that isn't positive can't be drawn. */
export const ELEMENT_SIZES: ReadonlySet<string> = new Set([
  'w',
  'h',
  'size',
  'width',
  'strokeWidth',
  'fontSize',
]);

export interface ElementBase {
  id: string;
  type: ElementType;
  frameId: string;
  /** Fractional index; elements are painted in ascending (z, id) order. */
  z: string;
  /** Top-left of the element's box, in world coordinates. */
  x: number;
  y: number;
  /** Degrees clockwise around (x, y), the convention Konva uses. */
  rotation: number;
  locked: boolean;
  groupId: string | null;
  /** User or guest id of the creator. */
  createdBy: string | null;
}

export interface StrokeElement extends ElementBase {
  type: 'stroke';
  tool: PenTool;
  color: string;
  size: number;
  /** Packed [x, y, pressure] points relative to (x, y); see points.ts. */
  points: Uint8Array;
}

export interface StickyElement extends ElementBase {
  type: 'sticky';
  w: number;
  h: number;
  color: string;
  text: string;
  font: FontFamily;
  /** Fixed font size, or null to shrink the text until it fits. */
  fontSize: number | null;
  align: TextAlign;
  author: string;
  /** Dot votes keyed by voter id (a nested Y.Map in the document). */
  votes: Record<string, true>;
}

export interface TextElement extends ElementBase {
  type: 'text';
  w: number;
  /** Measured height of the wrapped text, kept up to date by the editor. */
  h: number;
  text: string;
  color: string;
  fontSize: number;
  font: FontFamily;
  align: TextAlign;
  bold: boolean;
  italic: boolean;
}

export interface ShapeElement extends ElementBase {
  type: 'shape';
  kind: ShapeKind;
  w: number;
  h: number;
  fill: string;
  stroke: string;
  strokeWidth: number;
  dash: DashStyle;
  text: string;
  textColor: string;
  fontSize: number;
  font: FontFamily;
}

export interface ConnectorEndpoint {
  /** World position, used when unbound (and as a fallback if the target is gone). */
  x: number;
  y: number;
  /** Element this end is attached to. */
  bindId: string | null;
  /** Fixed attachment point in the target's box (0..1), or null to pick the facing edge. */
  anchor: Point | null;
}

export interface ConnectorElement extends ElementBase {
  type: 'connector';
  start: ConnectorEndpoint;
  end: ConnectorEndpoint;
  route: ConnectorRoute;
  startCap: ConnectorCap;
  endCap: ConnectorCap;
  color: string;
  width: number;
  dash: DashStyle;
}

export interface ImageElement extends ElementBase {
  type: 'image';
  w: number;
  h: number;
  assetId: string;
}

export interface StampElement extends ElementBase {
  type: 'stamp';
  emoji: string;
  size: number;
  author: string;
}

export type BoardElement =
  | StrokeElement
  | StickyElement
  | TextElement
  | ShapeElement
  | ConnectorElement
  | ImageElement
  | StampElement;

export type ElementOfType<T extends ElementType> = Extract<BoardElement, { type: T }>;

/** Properties of an element that may be changed after creation. */
export type ElementPatch = Partial<Omit<BoardElement, 'id' | 'type'>> & Record<string, unknown>;

// ---------------------------------------------------------------------------
// Frames and board metadata
// ---------------------------------------------------------------------------

export const BACKGROUND_PATTERNS = ['blank', 'dots', 'grid', 'lines'] as const;
export type BackgroundPattern = (typeof BACKGROUND_PATTERNS)[number];

export interface FrameBackground {
  pattern: BackgroundPattern;
  color: string;
}

export interface Frame {
  id: string;
  /** Fractional index of the frame in the frame strip. */
  order: string;
  name: string;
  background: FrameBackground;
}

export interface TimerState {
  /** Duration the timer was last set to. */
  durationMs: number;
  /** Epoch ms when a running timer ends; null while paused or stopped. */
  endsAt: number | null;
  /** Time left while paused. */
  remainingMs: number;
}

export interface BoardMeta {
  title: string;
  timer: TimerState | null;
}

export const DEFAULT_BACKGROUND: FrameBackground = { pattern: 'dots', color: '#ffffff' };

export const BACKGROUND_PRESETS: readonly {
  id: string;
  name: string;
  background: FrameBackground;
}[] = [
  { id: 'dots', name: 'Dots', background: { pattern: 'dots', color: '#ffffff' } },
  { id: 'blank', name: 'Blank', background: { pattern: 'blank', color: '#ffffff' } },
  { id: 'lines', name: 'Lined', background: { pattern: 'lines', color: '#ffffff' } },
  { id: 'grid', name: 'Grid', background: { pattern: 'grid', color: '#ffffff' } },
  { id: 'paper', name: 'Paper', background: { pattern: 'lines', color: '#fbf6e9' } },
  { id: 'dark', name: 'Dark', background: { pattern: 'dots', color: '#202124' } },
  { id: 'blueprint', name: 'Blueprint', background: { pattern: 'grid', color: '#1d3f6e' } },
  { id: 'chalkboard', name: 'Chalkboard', background: { pattern: 'blank', color: '#2f4a3a' } },
];

export const MAX_FRAMES = 100;

// ---------------------------------------------------------------------------
// Defaults and construction
// ---------------------------------------------------------------------------

export const DEFAULT_STICKY_SIZE = 200;

type TypeDefaults = {
  [T in ElementType]: Omit<ElementOfType<T>, Exclude<keyof ElementBase, 'type'>>;
};

function typeDefaults(): TypeDefaults {
  // A fresh object every call: defaults contain arrays/objects that must not be shared.
  return {
    stroke: { type: 'stroke', tool: 'pen', color: '#202124', size: 4, points: new Uint8Array() },
    sticky: {
      type: 'sticky',
      w: DEFAULT_STICKY_SIZE,
      h: DEFAULT_STICKY_SIZE,
      color: '#fff176',
      text: '',
      font: 'sans',
      fontSize: null,
      align: 'center',
      author: '',
      votes: {},
    },
    text: {
      type: 'text',
      w: 240,
      h: 32,
      text: '',
      color: '#202124',
      fontSize: 24,
      font: 'sans',
      align: 'left',
      bold: false,
      italic: false,
    },
    shape: {
      type: 'shape',
      kind: 'rect',
      w: 160,
      h: 120,
      fill: '#e8f0fe',
      stroke: '#1a73e8',
      strokeWidth: 3,
      dash: 'solid',
      text: '',
      textColor: '#202124',
      fontSize: 20,
      font: 'sans',
    },
    connector: {
      type: 'connector',
      start: { x: 0, y: 0, bindId: null, anchor: null },
      end: { x: 0, y: 0, bindId: null, anchor: null },
      route: 'straight',
      startCap: 'none',
      endCap: 'arrow',
      color: '#202124',
      width: 3,
      dash: 'solid',
    },
    image: { type: 'image', w: 320, h: 240, assetId: '' },
    stamp: { type: 'stamp', emoji: '👍', size: 64, author: '' },
  };
}

const BASE_DEFAULTS = {
  x: 0,
  y: 0,
  rotation: 0,
  locked: false,
  groupId: null,
  createdBy: null,
} as const;

export type ElementInit<T extends ElementType> = Partial<Omit<ElementOfType<T>, 'type'>> & {
  frameId: string;
};

/** Builds a complete element of `type`, filling every missing property with its default. */
export function createElement<T extends ElementType>(
  type: T,
  init: ElementInit<T>,
): ElementOfType<T> {
  const element = {
    id: newId(),
    z: 'a0',
    ...BASE_DEFAULTS,
    ...typeDefaults()[type],
    ...stripUndefined(init),
  };
  return element as unknown as ElementOfType<T>;
}

export function isElementType(value: unknown): value is ElementType {
  return typeof value === 'string' && (ELEMENT_TYPES as readonly string[]).includes(value);
}

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

function normalizeEndpoint(value: unknown, fallback: ConnectorEndpoint): ConnectorEndpoint {
  if (typeof value !== 'object' || value === null) return fallback;
  const v = value as Record<string, unknown>;
  const a = v.anchor as Record<string, unknown> | null | undefined;
  const anchorOk =
    typeof a === 'object' && a !== null && isFiniteNumber(a.x) && isFiniteNumber(a.y);
  if (
    isFiniteNumber(v.x) &&
    isFiniteNumber(v.y) &&
    (v.bindId === null || typeof v.bindId === 'string') &&
    (a === null || anchorOk)
  ) {
    // Already well-formed: keep the stored object, so unchanged ends stay identical.
    return value as ConnectorEndpoint;
  }
  return {
    x: isFiniteNumber(v.x) ? v.x : fallback.x,
    y: isFiniteNumber(v.y) ? v.y : fallback.y,
    bindId: typeof v.bindId === 'string' ? v.bindId : null,
    anchor: anchorOk ? { x: (a as unknown as Point).x, y: (a as unknown as Point).y } : null,
  };
}

/** `value` if it is the kind of thing `fallback` is, else `fallback`. */
function normalizeProperty(key: string, value: unknown, fallback: unknown): unknown {
  if (key in ELEMENT_ENUMS) {
    return ELEMENT_ENUMS[key]?.includes(value as string) ? value : fallback;
  }
  if (key === 'start' || key === 'end') {
    return normalizeEndpoint(value, fallback as ConnectorEndpoint);
  }
  if (key === 'points') return value instanceof Uint8Array ? value : fallback;
  if (key === 'votes') {
    return typeof value === 'object' && value !== null ? value : fallback;
  }
  if (fallback === null) {
    // groupId and createdBy are ids or null; a sticky's fontSize is a size or null.
    if (key === 'fontSize') return isFiniteNumber(value) && value > 0 ? value : null;
    return typeof value === 'string' ? value : null;
  }
  if (typeof fallback === 'number') {
    if (!isFiniteNumber(value)) return fallback;
    return ELEMENT_SIZES.has(key) && value <= 0 ? fallback : value;
  }
  return typeof value === typeof fallback ? value : fallback;
}

/**
 * Turns loosely-typed stored data into a complete element, filling defaults for
 * properties added after the element was written, and for any property that
 * holds the wrong kind of value: everyone with edit access writes to the
 * shared document, so what is read from it is never taken on trust. Returns
 * null for data that is not an element at all.
 */
export function normalizeElement(raw: Record<string, unknown>): BoardElement | null {
  const type = raw.type;
  if (!isElementType(type)) return null;
  if (typeof raw.id !== 'string' || typeof raw.frameId !== 'string') return null;
  const defaults: Record<string, unknown> = { z: 'a0', ...BASE_DEFAULTS, ...typeDefaults()[type] };
  const element: Record<string, unknown> = { ...defaults, ...stripUndefined(raw) };
  for (const [key, fallback] of Object.entries(defaults)) {
    if (key !== 'type') element[key] = normalizeProperty(key, element[key], fallback);
  }
  return element as unknown as BoardElement;
}

export function normalizeFrame(raw: Record<string, unknown>): Frame | null {
  if (typeof raw.id !== 'string') return null;
  const bg = (raw.background ?? {}) as Partial<FrameBackground>;
  return {
    id: raw.id,
    order: typeof raw.order === 'string' ? raw.order : 'a0',
    name: typeof raw.name === 'string' ? raw.name : '',
    background: {
      pattern: (BACKGROUND_PATTERNS as readonly string[]).includes(bg.pattern as string)
        ? (bg.pattern as BackgroundPattern)
        : DEFAULT_BACKGROUND.pattern,
      color: typeof bg.color === 'string' ? bg.color : DEFAULT_BACKGROUND.color,
    },
  };
}

export function createFrame(init: Partial<Frame> = {}): Frame {
  return {
    id: init.id ?? newId(),
    order: init.order ?? 'a0',
    name: init.name ?? '',
    background: { ...(init.background ?? DEFAULT_BACKGROUND) },
  };
}

function stripUndefined<T extends object>(obj: T): Partial<T> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value !== undefined) out[key] = value;
  }
  return out as Partial<T>;
}

/** Elements that have a width/height box (everything except strokes, connectors and stamps). */
export type BoxElement = StickyElement | TextElement | ShapeElement | ImageElement;

export function isBoxElement(el: BoardElement): el is BoxElement {
  return el.type === 'sticky' || el.type === 'text' || el.type === 'shape' || el.type === 'image';
}

/** Elements a connector can attach to. */
export function isConnectable(el: BoardElement): boolean {
  return el.type !== 'connector' && el.type !== 'stroke';
}
